import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TalentContentContainer from '../layout/TalentContentContainer';
import ArchiveHeader from './components/ArchiveHeader';
import ArchiveBoardView from './components/ArchiveBoardView';
import ArchiveListView from './components/ArchiveListView';
import ArchiveBoardDetail from './components/ArchiveBoardDetail';
import NewBoardModal from './components/NewBoardModal';
import {
  addBoardItem,
  createBoard, updateBoard, deleteBoard,
  getBoardItems, getAllSavedItems, moveBoardItem, removeBoardItem, getBoardPickerOptions,
} from '../../../api';
import { getCastingDetail } from '../../../api/castingApi';
import { formatCastingDDay } from '../../../utils/castingDeadline';

const PREFERRED_DEFAULT_BOARD_KEY = 'talent-archive-preferred-default';

function computeDDay(deadline) {
  return formatCastingDDay(deadline, { closedLabel: 'D-0', todayLabel: 'D-0' });
}

function mapArchiveItem(item) {
  const post = item.castingPost ?? item.casting ?? item.target ?? item ?? {};
  const province = post.regionProvince ?? post.shootingLocation ?? post.location ?? '';
  const city = post.regionCity ?? '';
  const deadline = post.applicationDeadline ?? post.deadline ?? null;
  return {
    id: item.id,
    castingId: item.targetId ?? post.id ?? null,
    category: post.projectType || post.category || '기타',
    savedDate: item.createdAt ? item.createdAt.slice(0, 10).replace(/-/g, '. ') : '',
    title: post.title || '공고 제목 없음',
    company: post.companyName ?? post.producerName ?? post.producerProfileName ?? post.producer?.companyName ?? '제작사 정보 없음',
    location: [province, city].filter(Boolean).join(' ') || '전국',
    deadline: deadline || '미정',
    dday: computeDDay(deadline),
    status: post.status || '',
    recruitmentCount: post.applicantCount ?? post.recruitmentCount ?? null,
  };
}

function isCastingItem(item) {
  return !item?.targetType || item.targetType === 'CASTING_POST';
}

async function hydrateArchiveItems(items = []) {
  return Promise.all(items.map(async (item) => {
    if (
      item.title &&
      item.title !== '공고 제목 없음' &&
      item.company &&
      item.company !== '제작사 정보 없음' &&
      item.deadline &&
      item.location
    ) return item;
    if (!item.castingId) return item;
    try {
      const detail = await getCastingDetail(item.castingId);
      return {
        ...item,
        category: detail.projectType || detail.category || item.category || '기타',
        title: detail.title || item.title || '공고 제목 없음',
        company: detail.companyName || item.company || '제작사 정보 없음',
        location: [detail.regionProvince, detail.regionCity].filter(Boolean).join(' ') || item.location || '전국',
        deadline: detail.applicationDeadline || item.deadline || '미정',
        dday: computeDDay(detail.applicationDeadline || item.deadline),
        status: detail.status || item.status || '',
        recruitmentCount: detail.applicantCount ?? item.recruitmentCount ?? null,
      };
    } catch {
      return item;
    }
  }));
}

function dedupeArchiveItems(items = []) {
  const seen = new Set();
  return items.filter((item) => {
    const key = item.castingId ?? item.id ?? `${item.title}-${item.savedDate}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function readPreferredDefaultBoardId() {
  try {
    return localStorage.getItem(PREFERRED_DEFAULT_BOARD_KEY);
  } catch {
    return null;
  }
}

function writePreferredDefaultBoardId(boardId) {
  try {
    if (!boardId) {
      localStorage.removeItem(PREFERRED_DEFAULT_BOARD_KEY);
      return;
    }
    localStorage.setItem(PREFERRED_DEFAULT_BOARD_KEY, String(boardId));
  } catch {}
}

export default function TalentArchivePage() {
  const navigate = useNavigate();
  const [view, setView] = useState('board');
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [editBoard, setEditBoard] = useState(null);
  const [boards, setBoards] = useState([]);
  const [boardItems, setBoardItems] = useState([]);
  const [allItems, setAllItems] = useState([]);

  const loadBoardItems = useCallback(async (boardId) => {
    if (!boardId) {
      setBoardItems([]);
      return [];
    }
    try {
      const items = await getBoardItems(boardId);
      const normalized = await hydrateArchiveItems(dedupeArchiveItems((items ?? []).filter(isCastingItem).map(mapArchiveItem)));
      setBoardItems(normalized);
      return normalized;
    } catch {
      setBoardItems([]);
      return [];
    }
  }, []);

  const loadAllCastingItems = useCallback(async () => {
    try {
      const items = await getAllSavedItems();
      const normalized = await hydrateArchiveItems(dedupeArchiveItems((items || []).filter(isCastingItem).map(mapArchiveItem)));
      setAllItems(normalized);
      return normalized;
    } catch {
      setAllItems([]);
      return [];
    }
  }, []);

  const fetchBoards = useCallback(async () => {
    try {
      const preferredDefaultBoardId = readPreferredDefaultBoardId();
      const normalized = await getBoardPickerOptions({
        targetType: 'CASTING_POST',
        preferredDefaultBoardId,
      });
      setBoards(normalized);
      return normalized;
    } catch {
      setBoards([]);
      return [];
    }
  }, []);

  useEffect(() => {
    const openBoardId = (() => {
      try {
        const v = localStorage.getItem('talent-archive-open-board');
        if (v) { localStorage.removeItem('talent-archive-open-board'); }
        return v;
      } catch { return null; }
    })();
    fetchBoards().then(normalized => {
      if (openBoardId) {
        const target = (normalized || []).find(b => String(b.id) === String(openBoardId));
        if (target) {
          setSelectedBoard(target);
          setView('detail');
          loadBoardItems(target.id);
        }
      }
    });
    loadAllCastingItems();
  }, [fetchBoards, loadAllCastingItems, loadBoardItems]);

  useEffect(() => {
    if (view === 'list') {
      loadAllCastingItems();
    }
  }, [view, loadAllCastingItems]);

  const totalCount = allItems.length;

  const handleBoardClick = async (b) => {
    setSelectedBoard(b);
    setView('detail');
    await loadBoardItems(b.id);
  };

  const handleRemoveBoardItem = async (itemId) => {
    try {
      await removeBoardItem(itemId);
      await Promise.all([
        selectedBoard?.id ? loadBoardItems(selectedBoard.id) : Promise.resolve([]),
        loadAllCastingItems(),
        fetchBoards(),
      ]);
    } catch {}
  };

  const handleRemoveAllItem = async (itemId) => {
    try {
      await removeBoardItem(itemId);
      setAllItems((prev) => prev.filter((i) => i.id !== itemId));
      setBoardItems((prev) => prev.filter((i) => i.id !== itemId));
      await fetchBoards();
    } catch {}
  };

  const handleMoveItem = async (item, boardId) => {
    try {
      await moveBoardItem(item.id, boardId);
      await Promise.all([
        selectedBoard?.id ? loadBoardItems(selectedBoard.id) : Promise.resolve([]),
        loadAllCastingItems(),
        fetchBoards(),
      ]);
    } catch {
      if (!item?.castingId) return;
      try {
        await addBoardItem({ boardId, targetType: 'CASTING_POST', targetId: Number(item.castingId) });
        await removeBoardItem(item.id);
        await Promise.all([
          selectedBoard?.id ? loadBoardItems(selectedBoard.id) : Promise.resolve([]),
          loadAllCastingItems(),
          fetchBoards(),
        ]);
      } catch {}
    }
  };

  const handleCreate = async ({ name, desc, emoji, color }) => {
    try {
      await createBoard({ name, color: color || null, emoji: emoji || null, description: desc || '' });
      await fetchBoards();
    } catch {}
    setShowNewModal(false);
  };

  const handleEdit = async ({ name, desc, emoji, color }) => {
    if (!editBoard) return;
    try {
      await updateBoard(editBoard.id, { name, color: color || null, emoji: emoji || null, description: desc || '' });
      await fetchBoards();
    } catch {}
    setEditBoard(null);
  };

  const handleDelete = async (board) => {
    if (board.isDefault) return;
    try {
      await deleteBoard(board.id);
      if (String(readPreferredDefaultBoardId()) === String(board.id)) {
        writePreferredDefaultBoardId(null);
      }
      setBoards(prev => prev.filter(b => b.id !== board.id));
    } catch {}
  };

  const handleSetDefaultBoard = async (board) => {
    if (!board?.id) return;
    writePreferredDefaultBoardId(board.id);
    await fetchBoards();
  };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <TalentContentContainer>
        {view === 'detail' ? (
          <ArchiveBoardDetail
            board={selectedBoard}
            auditions={boardItems}
            boards={boards}
            onBack={() => setView('board')}
            onRemove={handleRemoveBoardItem}
            onMove={handleMoveItem}
            onOpen={(castingId) => navigate(`/castings/${castingId}`)}
          />
        ) : (
          <>
            <ArchiveHeader
              view={view}
              totalCount={totalCount}
              onBoardView={() => setView('board')}
              onListView={() => setView('list')}
              onNewBoard={() => setShowNewModal(true)}
            />
            <div style={{ padding: '0 40px 80px' }}>
              {view === 'board' ? (
                <ArchiveBoardView
                  boards={boards}
                  onBoardClick={handleBoardClick}
                  onEditBoard={(b) => setEditBoard(b)}
                  onDeleteBoard={handleDelete}
                  onSetDefaultBoard={handleSetDefaultBoard}
                />
              ) : (
                <ArchiveListView
                  auditions={allItems}
                  boards={boards}
                  onRemove={handleRemoveAllItem}
                  onMove={handleMoveItem}
                  onOpen={(castingId) => navigate(`/castings/${castingId}`)}
                />
              )}
            </div>
          </>
        )}
      </TalentContentContainer>

      {showNewModal && (
        <NewBoardModal onClose={() => setShowNewModal(false)} onCreate={handleCreate} />
      )}
      {editBoard && (
        <NewBoardModal initialData={editBoard} onClose={() => setEditBoard(null)} onCreate={handleEdit} />
      )}
    </div>
  );
}
